<template>
  <!-- Scrollable content -->
  <nav class="relative mb-2">
    <div
      v-if="!hideBorder"
      class="absolute bottom-0 w-full h-px bg-slate-200 dark:bg-slate-700"
      aria-hidden="true"
    ></div>
    <ul
      :class="
        listContainerClass ||
        'relative text-sm font-medium flex flex-nowrap -mx-4 sm:-mx-6 lg:-mx-8 overflow-x-scroll no-scrollbar'
      "
    >
      <li
        v-for="item in items"
        :key="
          props.optionLabel && typeof item !== 'string'
            ? item[props.optionLabel]
            : `${item}`
        "
        class="mr-6 last:mr-0 first:pl-4 sm:first:pl-6 lg:first:pl-8 last:pr-4 sm:last:pr-6 lg:last:pr-8"
        @click="() => handleTabSelected(item)"
      >
        <button
          class="block pb-2 whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed capitalize"
          :disabled="disabled"
          :class="
            isSelected(item)
              ? 'text-primary-500 border-primary-500 border-b-2'
              : 'text-slate-500 dark:text-slate-400 hover:text-slate-600 dark:hover:text-slate-300'
          "
        >
          {{
            typeof item === "string" ? item : optionLabel ? item[optionLabel] : item.label
          }}
        </button>
      </li>
    </ul>
  </nav>
</template>

<script setup lang="ts" generic="EntryType extends string | DropdownOption">
/* __placeholder__ */
import type { DropdownOption } from "@/types/formData";
type Key = keyof EntryType;
const props = defineProps<
  {
    items: EntryType[];
    hideBorder?: boolean;
    disabled?: boolean;
    optionLabel?: "label" | "value";
    optionValue?: Key;
    listContainerClass?: string;
  } & (
    | {
        optionValue?: "value";
        selectedItem?: EntryType[Key];
      }
    | {
        optionValue?: undefined;
        selectedItem?: EntryType;
      }
  )
>();

const emit = defineEmits<{
  (name: "selected", payload: EntryType): void;
}>();

const selected = ref();

function handleTabSelected(item: EntryType) {
  setSelection(item);
  emit("selected", item);
}

function setSelection(item: EntryType) {
  if (typeof item === "object" && props.optionValue) {
    selected.value = item[props.optionValue];
  } else {
    selected.value = item;
  }
}

function isSelected(item: EntryType) {
  const active = selected.value;
  if (typeof item !== "string" && props.optionValue) {
    return item[props.optionValue] === active;
  } else if (typeof item !== "string") {
    return item.value === active?.value;
  }

  return item === active;
}

onBeforeMount(() => {
  const firstOption = props.items[0];
  if (props.selectedItem && props.optionValue) {
    selected.value = props.selectedItem;
  } else if (
    props.selectedItem &&
    typeof props.selectedItem === "string" &&
    typeof firstOption === "string"
  ) {
    const found = props.items.find(i => i === props.selectedItem);
    if (found) setSelection(found);
  } else if (
    props.selectedItem &&
    typeof props.selectedItem !== "string" &&
    typeof firstOption !== "string"
  ) {
    setSelection(props.selectedItem as EntryType);
  } else if (!props.selectedItem) {
    setSelection(firstOption);
  }
});
</script>
